import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { queue, sendChat } from "../services/chatService.js";
import {
    CATEGORIES,
    CATEGORIES_DIV,
    LIVE_AGENT_UNAVAILABLE_MSG,
    PROMOTION_MSG,
    SOURCE_CHAT,
    WAIT_MSG,
    TRY_MY_BEST_MSG,
    QUEUE_RETRY_COUNT,
    SURVEY_AGENT,
    CONNECTED_LIVE_AGENT_MSG
} from "../../constants.js";
import { genesysRefresh, genesysRequest, genesysSend } from "../services/liveAgentService.js";
import { handoverLiveAgent } from "../services/apiService.js";
import { formatMessageData } from "../utils/formatData.js";
import {
    clearQueuePos,
    setQueuePos,
    setQueueScheduler,
    setConnectedLiveAgent,
    setGenesysDetails,
    setLiveAgentUnavailable,
    setRetryLiveAgent,
    setConnectingLiveAgent,
} from "../features/liveAgentSlice.js";
import {
    concatMessage,
    setMessages,
    setLastMessagePosition,
    incrementPosition,
    updateExpiredAt,
    setLoadingReply
} from "../features/messagesSlice.js";
import { setSurveyLinkState } from "../features/surveySlice.js";

export default function useSendData() {
    const dispatch = useDispatch();
    const position = useSelector((state) => state.messages.position);
    const queuePos = useSelector((state) => state.liveAgent.queuePos);
    const messages = useSelector((state) => state.messages.messages);
    const deviceId = useSelector((state) => state.session.deviceId);
    const sKey = useSelector((state) => state.session.sKey);
    const token = useSelector((state) => state.session.token);
    const connectedLiveAgent = useSelector(state => state.liveAgent.connectedLiveAgent);
    const retryLiveAgent = useSelector(state => state.liveAgent.retryLiveAgent);
    const genesysDetails = useSelector(state => state.liveAgent.genesysDetails);
    const fullName = useSelector(state => state.userDetails.fullName);
    const mobileNumber = useSelector(state => state.userDetails.mobileNumber);
    const pvpbFlag = useSelector(state => state.userDetails.pvpbFlag);
    const cifId = useSelector(state => state.userDetails.cifId);
    const idNumber = useSelector(state => state.userDetails.idNumber);
    const mob_islogin = useSelector(state => state.userDetails.mob_islogin);
    const shadowRoot = document.getElementById("app").shadowRoot;

    let queueRetryCount = 0;
    let mob_inquiryselection;
    let mob_routingcategory;
    const [categoryMessage, setCategoryMessage] = useState(null);

    const sendMessage = async (userInput, userInputType = "normal") => {

        if (!userInput) return;

        const userMessageData = formatMessageData(deviceId, "user", userInput, position, userInputType);
        dispatch(concatMessage(userMessageData));
        dispatch(incrementPosition());
        dispatch(updateExpiredAt());

        if (userInputType === "normal" && userInput.toLowerCase() === "chat now" || retryLiveAgent) {
            const allMessages = [...messages, userMessageData];
            let triggerLiveAgent = false;

            if (allMessages.filter((msg) => msg.content_type === "normal" && msg.content.toLowerCase() === "chat now" && msg.content.toLowerCase() === "chat now" && msg.user_id === "user").length === 3) {
                triggerLiveAgent = true;
            } else if (messages[ messages.length - 1 ]?.content.toLowerCase().includes("chat now")) {
                triggerLiveAgent = true;
            }

            // Handle live agent logic
            if (triggerLiveAgent || retryLiveAgent) {

                const tempMessage = formatMessageData(
                    deviceId,
                    "bot",
                    `Please click one of the category below: <br>
                     <span class="select-category" id="category-casa-fd">1. ${CATEGORIES[ CATEGORIES_DIV[ "category-casa-fd" ] ]}</span><br>
                     <span class="select-category" id="category-report-lost">2. ${CATEGORIES[ CATEGORIES_DIV[ "category-report-lost" ] ]}</span><br>
                     <span class="select-category" id="category-auto-loan">3. ${CATEGORIES[ CATEGORIES_DIV[ "category-auto-loan" ] ]}</span><br>
                     <span class="select-category" id="category-personal-loan">4. ${CATEGORIES[ CATEGORIES_DIV[ "category-personal-loan" ] ]}</span><br>
                     <span class="select-category" id="category-mortgage">5. ${CATEGORIES[ CATEGORIES_DIV[ "category-mortgage" ] ]}</span><br>
                     <span class="select-category" id="category-debit-card">6. ${CATEGORIES[ CATEGORIES_DIV[ "category-debit-card" ] ]}</span><br>
                     <span class="select-category" id="category-credit-card">7. ${CATEGORIES[ CATEGORIES_DIV[ "category-credit-card" ] ]}</span><br>
                     <span class="select-category" id="category-hlb-connect">8. ${CATEGORIES[ CATEGORIES_DIV[ "category-hlb-connect" ] ]}</span>`,
                    position + 1);
                setCategoryMessage(tempMessage);
                dispatch(incrementPosition());
                dispatch(updateExpiredAt());
                dispatch(concatMessage(tempMessage));
                dispatch(setConnectingLiveAgent(true));
            } else {
                const message = formatMessageData(deviceId, "bot", TRY_MY_BEST_MSG, position + 1);
                dispatch(concatMessage(message));
                dispatch(incrementPosition());
                dispatch(updateExpiredAt());
                dispatch(setLoadingReply(false));
            }

        } else if (connectedLiveAgent) {
            await sendMessageToAgent(userInput, genesysDetails);
        } else {
            await sendMessageToAI(userInput);
        }
    };

    const sendMessageToAgent = async (userInput, genesysDetails) => {
        try {
            dispatch(setLoadingReply(false));
            await genesysSend(
                genesysDetails.chatId,
                genesysDetails.userId,
                genesysDetails.secureKey,
                genesysDetails.alias,
                userInput
            );
            dispatch(setLastMessagePosition(messages.length + 1));
        } catch {
            console.error("Error sending message");
        }
    };

    const sendMessageToAI = async (userInput) => {
        try {
            await sendChat(deviceId, token, sKey, userInput, position + 1, dispatch);
            dispatch(incrementPosition());
        } catch (error) {
            console.error(error);
            return "An error has occurrred, please try again later.";
        }
    };

    useEffect(() => {
        if (categoryMessage) {
            const casaFDElements = shadowRoot.querySelectorAll("#category-casa-fd");
            casaFDElements[ casaFDElements.length - 1 ].addEventListener("click", () => handleCategory("category-casa-fd"));
            const reportLostElements = shadowRoot.querySelectorAll("#category-report-lost");
            reportLostElements[ reportLostElements.length - 1 ].addEventListener("click", () => handleCategory("category-report-lost"));
            const autoLoanElements = shadowRoot.querySelectorAll("#category-auto-loan");
            autoLoanElements[ autoLoanElements.length - 1 ].addEventListener("click", () => handleCategory("category-auto-loan"));
            const personalLoanElements = shadowRoot.querySelectorAll("#category-personal-loan");
            personalLoanElements[ personalLoanElements.length - 1 ].addEventListener("click", () => handleCategory("category-personal-loan"));
            const mortgageElements = shadowRoot.querySelectorAll("#category-mortgage");
            mortgageElements[ mortgageElements.length - 1 ].addEventListener("click", () => handleCategory("category-mortgage"));
            const debitCardElements = shadowRoot.querySelectorAll("#category-debit-card");
            debitCardElements[ debitCardElements.length - 1 ].addEventListener("click", () => handleCategory("category-debit-card"));
            const creditCardElements = shadowRoot.querySelectorAll("#category-credit-card");
            creditCardElements[ creditCardElements.length - 1 ].addEventListener("click", () => handleCategory("category-credit-card"));
            const hlbConnectElements = shadowRoot.querySelectorAll("#category-hlb-connect");
            hlbConnectElements[ hlbConnectElements.length - 1 ].addEventListener("click", () => handleCategory("category-hlb-connect"));
        }

    }, [categoryMessage]);

    const handleCategory = async (category) => {
        const categoryKey = CATEGORIES_DIV[ category ];
        mob_inquiryselection = CATEGORIES[ categoryKey ];
        mob_routingcategory = categoryKey;

        const categoryElement = shadowRoot.querySelectorAll(".select-category");
        categoryElement.forEach(element => element.replaceWith(element.cloneNode(true)));
        const message = formatMessageData(deviceId, "bot", WAIT_MSG, position);
        dispatch(concatMessage(message));
        dispatch(incrementPosition());
        dispatch(updateExpiredAt());
        await genesysConnection();
    };

    const genesysConnection = async () => {
        let allMessages = [
            ...messages
        ];
        try {
            const genesysRequestResponse = await genesysRequest(fullName, mob_islogin, cifId, idNumber, mob_inquiryselection, mob_routingcategory, SOURCE_CHAT, "English", "en-MY", pvpbFlag, mobileNumber);
            dispatch(setGenesysDetails(genesysRequestResponse));
            handoverLiveAgent(genesysRequestResponse.chatId, genesysRequestResponse.userId, token);
            // Get Queue
            const getQueuePosition = setInterval(async () => {
                if (queueRetryCount === QUEUE_RETRY_COUNT && queuePos === null) {
                    queueRetryCount = 0;
                    clearInterval(getQueuePosition);
                    const message = formatMessageData(deviceId, "bot", LIVE_AGENT_UNAVAILABLE_MSG, position);
                    dispatch(concatMessage(message));
                    dispatch(incrementPosition());
                    allMessages = [...allMessages, message];
                    dispatch(updateExpiredAt());
                    dispatch(setLiveAgentUnavailable(true));
                    dispatch(setRetryLiveAgent(false));
                }
                try {
                    const queueResponse = await queue(token, genesysRequestResponse.chatId);
                    if (queueResponse) {
                        dispatch(setQueuePos(queueResponse[ Object.keys(queueResponse)[ 0 ] ].pos));
                    }
                } catch {
                    try {
                        const genesysRefreshResponse = await genesysRefresh(
                            genesysRequestResponse.chatId,
                            genesysRequestResponse.userId,
                            genesysRequestResponse.secureKey,
                            genesysRequestResponse.alias,
                        );
                        const connectedToLiveAgent = genesysRefreshResponse.messages.some(message =>
                            message.type === "ParticipantJoined" && message.from.type === "Agent"
                        );

                        const interactionId = genesysRefreshResponse.chatId
                        dispatch(setSurveyLinkState(`${SURVEY_AGENT}?InteractionID=${interactionId}`));
                        if (connectedToLiveAgent) {
                            clearInterval(getQueuePosition);
                            dispatch(clearQueuePos());
                            dispatch(setConnectedLiveAgent(true));
                            dispatch(setLoadingReply(false));
                            const connectedMessage = formatMessageData(deviceId, "bot", CONNECTED_LIVE_AGENT_MSG, position);
                            dispatch(concatMessage(connectedMessage));
                            dispatch(incrementPosition());
                            dispatch(updateExpiredAt());
                        } else {
                            queueRetryCount++;
                        }
                    } catch {
                        console.log("Refresh error");
                    }
                }
            }, 5000);
            dispatch(setQueueScheduler(getQueuePosition));
        } catch (error) {
            console.error(error);
            return "An error has occurrred, please try again later.";
        }
    };

    useEffect(() => {
        if (queuePos) {
            if (messages.every(msg => msg.content !== PROMOTION_MSG)) {
                const message = formatMessageData(deviceId, "bot", PROMOTION_MSG, position);
                dispatch(setMessages([
                    ...messages,
                    message
                ]));
                dispatch(incrementPosition());
                dispatch(updateExpiredAt());
            }
        }
    }, [queuePos]);

    return { sendMessage };
};
